import { useCallback, useEffect, useMemo, useState } from "react";
import {
  applyResultFilters,
  fetchStockByTenant,
  filterStockLines,
  formatStockTimestamp,
} from "../../api/stock";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { isAbandonedProduct, STOCK_STATUSES } from "./stockStatus";

/**
 * Charge le stock du tenant, filtre localement avec debounce + filtres UI.
 */
export function useStockSearch({ token, tenant, authError }) {
  const [stockLines, setStockLines] = useState([]);
  const [query, setQuery] = useState("");
  const [fileDate, setFileDate] = useState("");
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState("");
  const [scannerResult, setScannerResult] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showAbandoned, setShowAbandoned] = useState(false);

  const debouncedQuery = useDebouncedValue(query, 300);

  useEffect(() => {
    if (!token || !tenant) return;

    let cancelled = false;

    async function loadStock() {
      setStockLoading(true);
      setStockError("");
      try {
        const data = await fetchStockByTenant(token, tenant);
        if (cancelled) return;
        setStockLines(data.StockLineList || []);
        setFileDate(formatStockTimestamp(data?.Header?.Horodatage));
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setStockLines([]);
          setStockError(
            error.response?.data?.message ||
              error.message ||
              "Erreur lors du chargement du stock."
          );
        }
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    }

    loadStock();
    return () => {
      cancelled = true;
    };
  }, [token, tenant]);

  const matchedLines = useMemo(() => {
    const activeQuery = scannerResult || debouncedQuery;
    return filterStockLines(stockLines, activeQuery);
  }, [stockLines, debouncedQuery, scannerResult]);

  const availableStatuses = useMemo(() => {
    const present = new Set(
      matchedLines
        .filter((product) => !isAbandonedProduct(product) || showAbandoned)
        .map((product) => product.StockStatus)
        .filter((status) => STOCK_STATUSES.some((s) => s.id === status))
    );
    return STOCK_STATUSES.map((s) => s.id).filter((id) => present.has(id));
  }, [matchedLines, showAbandoned]);

  const { results, abandonedCount } = useMemo(
    () =>
      applyResultFilters(matchedLines, {
        stockStatuses: selectedStatuses,
        showAbandoned,
        isAbandoned: isAbandonedProduct,
      }),
    [matchedLines, selectedStatuses, showAbandoned]
  );

  // Retire les filtres de statut qui ne sont plus présents dans les résultats texte.
  useEffect(() => {
    setSelectedStatuses((current) =>
      current.filter((statusId) =>
        matchedLines.some((product) => product.StockStatus === statusId)
      )
    );
  }, [matchedLines]);

  const message = useMemo(() => {
    if (authError) return authError;
    if (stockError) return stockError;

    const activeQuery = (scannerResult || debouncedQuery).trim();
    if (!activeQuery || stockLoading) return "";

    const singularOrPlural = results.length > 1 ? "produits" : "produit";
    return results.length === 0
      ? "Il n'y a pas de résultat pour les critères sélectionnés."
      : `Vous avez ${results.length} ${singularOrPlural} correspondant à votre recherche.`;
  }, [
    authError,
    stockError,
    scannerResult,
    debouncedQuery,
    stockLoading,
    results.length,
  ]);

  const handleInputChange = useCallback((event) => {
    setScannerResult("");
    setQuery(event.target.value);
  }, []);

  const onScanBarcode = useCallback((code) => {
    setQuery(code);
    setScannerResult(code);
  }, []);

  const toggleStatus = useCallback((statusId) => {
    setSelectedStatuses((current) =>
      current.includes(statusId)
        ? current.filter((id) => id !== statusId)
        : [...current, statusId]
    );
  }, []);

  const toggleAbandoned = useCallback(() => {
    setShowAbandoned((current) => !current);
  }, []);

  return {
    query,
    results,
    message,
    loading: stockLoading,
    fileDate,
    scannerResult,
    handleInputChange,
    onScanBarcode,
    availableStatuses,
    selectedStatuses,
    toggleStatus,
    abandonedCount,
    showAbandoned,
    toggleAbandoned,
  };
}
